import React from "react";
import { Form, InputNumber, Select, DatePicker } from "antd";
import { PurchasePaymentStatus } from "../../../Types";

const { Option } = Select;

const currencyFormatter = (v: any) => `Rs. ${v}`;
const currencyParser = (v: any) => (v ? v.replace(/Rs\.\s?|(,*)/g, "") : "");

interface Props {
  grossTotal: number;
  grandTotal: number;
  dueAmount: number;
  globalDiscount: number;
  paidAmount: number;
  readOnly: boolean;
  onDiscountChange: (v: number) => void;
  onPaidChange: (v: number) => void;
}

export const PurchaseSummaryPanel: React.FC<Props> = ({
  grossTotal,
  grandTotal,
  dueAmount,
  globalDiscount,
  paidAmount,
  readOnly,
  onDiscountChange,
  onPaidChange,
}) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    {/* Left: Controls */}
    <div className="flex flex-col gap-2">
      <Form.Item label="Invoice Discount" className="mb-1">
        <InputNumber
          className="w-full"
          min={0}
          max={grossTotal}
          value={globalDiscount}
          disabled={readOnly}
          onChange={(v) => onDiscountChange(v || 0)}
          formatter={currencyFormatter}
          parser={currencyParser as any}
        />
      </Form.Item>

      <Form.Item label="Amount Paid" className="mb-1">
        <InputNumber
          className="w-full"
          min={0}
          value={paidAmount}
          disabled={readOnly}
          onChange={(v) => onPaidChange(v || 0)}
          formatter={currencyFormatter}
          parser={currencyParser as any}
        />
      </Form.Item>
    </div>

    {/* Center: Status & Due Date */}
    <div className="flex flex-col gap-2">
      <Form.Item label="Payment Status" name="payment_status" className="mb-1">
        <Select disabled>
          <Option value={PurchasePaymentStatus.PAID}>
            {PurchasePaymentStatus.PAID}
          </Option>
          <Option value={PurchasePaymentStatus.PARTIAL}>
            {PurchasePaymentStatus.PARTIAL}
          </Option>
          <Option value={PurchasePaymentStatus.DUE}>
            {PurchasePaymentStatus.DUE}
          </Option>
        </Select>
      </Form.Item>

      {dueAmount > 0 && (
        <Form.Item
          label="Payment Due Date"
          name="due_date"
          className="mb-1"
          rules={[
            {
              required: dueAmount > 0,
              message: "Required for unpaid balances",
            },
          ]}
        >
          <DatePicker
            className="w-full"
            format="YYYY-MM-DD"
            disabled={readOnly}
          />
        </Form.Item>
      )}
    </div>

    {/* Right: Totals summary card */}
    <div className="bg-gray-50 p-4 rounded-md border border-gray-100 flex flex-col gap-2">
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>Gross Total</span>
        <span>Rs. {grossTotal.toFixed(2)}</span>
      </div>
      <div className="flex items-center justify-between text-xs text-gray-500 border-b border-gray-100 pb-2">
        <span>Discount</span>
        <span className="text-red-400">-Rs. {globalDiscount.toFixed(2)}</span>
      </div>
      <div className="flex items-center justify-between text-sm font-semibold text-gray-800">
        <span>Grand Total</span>
        <span>Rs. {grandTotal.toFixed(2)}</span>
      </div>
      <div className="flex items-center justify-between text-xs text-green-600 border-b border-gray-100 pb-2">
        <span>Paid</span>
        <span>Rs. {paidAmount.toFixed(2)}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="font-semibold text-gray-800 text-xs uppercase tracking-wide">
          Due
        </span>
        <span
          className={`text-base font-bold ${dueAmount > 0 ? "text-red-500" : "text-green-600"}`}
        >
          Rs. {dueAmount.toFixed(2)}
        </span>
      </div>
    </div>
  </div>
);
